import { eventChannel } from 'redux-saga';
import { call, put, take, takeLatest } from 'redux-saga/effects';
import type { Activity } from '@nx/domain';
import { activityRepository } from '../../services/eoRepositories';
import { pushToast } from '../slices/eoUiSlice';
import {
  activitiesFailed,
  activitiesLoaded,
  activitiesWatchRequested,
  activityCreateRequested,
  activityDeleteRequested,
  activityReorderRequested,
  activitySaveSucceeded,
  activityUpdateRequested,
} from '../slices/activitiesSlice';

function* watchActivities(eventId: string): Generator<unknown, void, unknown> {
  const channel = eventChannel<Activity[]>((emit) =>
    activityRepository.watchByEvent(eventId, (items) => emit(items)),
  );
  try {
    while (true) {
      const items = (yield take(channel)) as Activity[];
      yield put(activitiesLoaded(items));
    }
  } finally {
    channel.close();
  }
}

function* handleWatch(action: ReturnType<typeof activitiesWatchRequested>): Generator<unknown, void, unknown> {
  yield call(watchActivities, action.payload);
}

function* handleCreate(action: ReturnType<typeof activityCreateRequested>): Generator<unknown, void, unknown> {
  try {
    yield call(
      [activityRepository, activityRepository.create],
      action.payload.eventId,
      action.payload.input,
    );
    yield put(activitySaveSucceeded());
    yield put(pushToast({ msg: 'eo.toast.activitySaved', kind: 'success' }));
  } catch (e) {
    yield put(activitiesFailed(e instanceof Error ? e.message : 'Failed to save activity'));
    yield put(pushToast({ msg: 'eo.toast.activitySaveFailed', kind: 'danger' }));
  }
}

function* handleUpdate(action: ReturnType<typeof activityUpdateRequested>): Generator<unknown, void, unknown> {
  try {
    yield call(
      [activityRepository, activityRepository.update],
      action.payload.eventId,
      action.payload.activityId,
      action.payload.input,
    );
    yield put(activitySaveSucceeded());
    yield put(pushToast({ msg: 'eo.toast.activityUpdated', kind: 'success' }));
  } catch (e) {
    yield put(activitiesFailed(e instanceof Error ? e.message : 'Failed to update activity'));
  }
}

function* handleDelete(action: ReturnType<typeof activityDeleteRequested>): Generator<unknown, void, unknown> {
  try {
    yield call(
      [activityRepository, activityRepository.delete],
      action.payload.eventId,
      action.payload.activityId,
    );
    yield put(pushToast({ msg: 'eo.toast.activityDeleted', kind: 'success' }));
  } catch (e) {
    yield put(pushToast({ msg: 'eo.toast.activityDeleteFailed', kind: 'danger' }));
  }
}

function* handleReorder(action: ReturnType<typeof activityReorderRequested>): Generator<unknown, void, unknown> {
  try {
    yield call(
      [activityRepository, activityRepository.reorder],
      action.payload.eventId,
      action.payload.date,
      action.payload.orderedIds,
    );
  } catch (e) {
    yield put(pushToast({ msg: 'eo.toast.activityReorderFailed', kind: 'danger' }));
  }
}

export function* activitiesSaga() {
  yield takeLatest(activitiesWatchRequested.type, handleWatch);
  yield takeLatest(activityCreateRequested.type, handleCreate);
  yield takeLatest(activityUpdateRequested.type, handleUpdate);
  yield takeLatest(activityDeleteRequested.type, handleDelete);
  yield takeLatest(activityReorderRequested.type, handleReorder);
}
